import { SearchBoxSuggestion } from '@mapbox/search-js-core';

import { LngLatObj } from '../types';

// The [IETF language tag](https://en.wikipedia.org/wiki/IETF_language_tag) to be returned.
// List of languages that Strava supports, can check the following link all the languages
// supported by [Mapbox Search Box API](https://docs.mapbox.com/api/search/search-box/#supported-languages)
const supportedLanguageValues = [
  'de',
  'en',
  'es',
  'fr',
  'id',
  'it',
  'ja',
  'nl',
  'pt',
  'ru',
  'zh'
];

/**
 * Converts a language string to an IETF language tag.
 * If the language is not supported, it defaults to 'en'.
 */
export const languageMapToIETFTag = (lang?: string) => {
  if (!lang || typeof lang !== 'string') {
    return 'en';
  }

  const normalized = lang.trim().toLowerCase();
  const baseTag = normalized.split('-')[0] ?? '';

  return supportedLanguageValues.includes(baseTag) ? baseTag : 'en';
};

/**
 * Normalizes the search query to ensure it is in a format that Mapbox's API expects it to be
 */
export const normalizedSearchQuery = (value: string) => {
  const coords = getCoordinatePairs(value);

  return coords ? `${coords.lat},${coords.lng}` : value;
};

/**
 * Given a string returns the matching geographic coordinate(s) when there is a match.
 */
export const getCoordinatePairs = (value?: string | null): LngLatObj | null => {
  // Step 1: Simple extraction
  const match = value?.match(
    /^[ ]*(?:Lat:? ?)?(-?\d+(?:\.\d+)?)[, ]+(?:Lng:? ?)?(-?\d+(?:\.\d+)?)[ ]*$/i
  );
  if (!match || match[1] === undefined || match[2] === undefined) {
    return null;
  }

  // Step 2: Programmatic bounds checking
  const lat = parseFloat(match[1]);
  const lng = parseFloat(match[2]);
  if (lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
    return { lat, lng };
  }

  return null;
};

/**
 * Returns a coordinate feature in the format expected by the SearchBox component.
 */
const coordinateFeature = (lng: number, lat: number) => {
  return {
    name: `Lat: ${lat}, Lng: ${lng}`,
    // TODO: we might need to translate this...
    place_formatted: 'Coordinate location',
    full_address: `Lat: ${lat}, Lng: ${lng}`,
    _geometry: {
      type: 'Point',
      coordinates: [lng, lat]
    }
  } as SearchBoxSuggestion;
};

/**
 * Custom search function to return artificial coordinate results
 * for use with the customSearch option in the SearchBox component.
 */
export const searchBoxCustomSearch = async (
  query: string
): Promise<SearchBoxSuggestion[]> => {
  const coords = getCoordinatePairs(query);
  if (coords === null) {
    // @ts-expect-error - types in search box do not allow this, however the library
    // actually supports it
    return null;
  }

  const coord1 = coords.lng;
  const coord2 = coords.lat;
  const coordsFeatures: SearchBoxSuggestion[] = [];

  if (coord1 < -90 || coord1 > 90) {
    // must be lng, lat
    coordsFeatures.push(coordinateFeature(coord1, coord2));
  }

  if (coord2 < -90 || coord2 > 90) {
    // must be lat, lng
    coordsFeatures.push(coordinateFeature(coord2, coord1));
  }

  if (coordsFeatures.length === 0) {
    // else could be either lng, lat or lat, lng
    coordsFeatures.push(coordinateFeature(coord1, coord2));
    coordsFeatures.push(coordinateFeature(coord2, coord1));
  }

  return coordsFeatures;
};
